import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPublicPreview } from '@/api/teleopApi';
import LiveKitPublicViewerProvider from '@/providers/LiveKitPublicViewerProvider';
import type { PublicPreviewResponse } from '@/types';
import { logWarn } from '@/lib/logger';

const PREVIEW_REFRESH_MS = 10000;
const DEFAULT_RECORDED_PREVIEW_URL = '/final_social.mp4';
const RECORDED_PREVIEW_URL =
  import.meta.env.VITE_PUBLIC_PREVIEW_RECORDING_URL || DEFAULT_RECORDED_PREVIEW_URL;

type PublicViewerStatus = 'idle' | 'connecting' | 'connected' | 'failed';

const LandingPreview: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [preview, setPreview] = useState<PublicPreviewResponse | null>(null);
  const [previewErrorMessage, setPreviewErrorMessage] = useState<string | null>(null);
  const [viewerStatus, setViewerStatus] = useState<PublicViewerStatus>('idle');

  const loadPreview = useCallback(async () => {
    try {
      const nextPreview = await fetchPublicPreview();
      setPreview(nextPreview);
      setPreviewErrorMessage(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Preview unavailable';
      setPreviewErrorMessage(errorMessage);
      logWarn('public_preview.state_failed', 'Public preview state failed', {
        error: String(error),
      });
    }
  }, []);

  useEffect(() => {
    void loadPreview();
    const intervalId = window.setInterval(() => {
      void loadPreview();
    }, PREVIEW_REFRESH_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadPreview]);

  const isLivePreview =
    preview?.mode === 'live' &&
    Boolean(preview.robot_id) &&
    Boolean(preview.room_id);
  const liveRobotId = preview?.mode === 'live' ? preview.robot_id : null;
  const liveRoomId = preview?.mode === 'live' ? preview.room_id : null;
  const statusLabel = isLivePreview
    ? viewerStatus === 'connected'
      ? 'Live now'
      : 'Connecting live feed'
    : 'Demo replay';
  const previewFrameClassName = isLivePreview
    ? 'relative aspect-video w-full bg-black'
    : 'relative mx-auto aspect-[9/16] max-h-[62svh] w-full max-w-[22rem] bg-black sm:max-w-[26rem]';
  const videoClassName = isLivePreview
    ? 'h-full w-full object-cover'
    : 'h-full w-full object-contain';

  return (
    <section
      aria-label="Play Claw preview"
      className="w-full overflow-hidden rounded-2xl border border-white/12 bg-black/35 text-left shadow-[0_24px_80px_rgba(0,0,0,0.35)] backdrop-blur-md"
    >
      <div className={previewFrameClassName}>
        {isLivePreview && liveRobotId && liveRoomId ? (
          <>
            <video
              ref={videoRef}
              className={videoClassName}
              autoPlay
              muted
              playsInline
            />
            <LiveKitPublicViewerProvider
              robotId={liveRobotId}
              roomName={liveRoomId}
              videoRef={videoRef}
              onStatusChange={setViewerStatus}
            />
          </>
        ) : (
          <video
            className={videoClassName}
            src={RECORDED_PREVIEW_URL}
            autoPlay
            muted
            loop
            playsInline
          />
        )}
        <div className="absolute left-3 top-3 rounded-full border border-white/15 bg-black/65 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-white">
          {statusLabel}
        </div>
      </div>
      <div className="grid gap-2 px-4 py-3 sm:grid-cols-[1fr_auto] sm:items-center">
        <p className="text-sm leading-6 text-white/78">
          Watch the real claw floor when someone is playing or waiting. When the robot is idle, this panel shows a
          replay so visitors can see the product before signing in.
        </p>
        {preview?.mode === 'live' ? (
          <div className="text-sm font-medium text-amber-100">
            Queue: {preview.queue_depth}
          </div>
        ) : null}
        {previewErrorMessage ? (
          <p className="text-xs text-amber-100/80 sm:col-span-2">
            Preview status is temporarily unavailable.
          </p>
        ) : null}
      </div>
    </section>
  );
};

export default LandingPreview;
